/**
 * WebSocket-based chat hook for Workflow execution.
 *
 * Drop-in replacement for useToolExecution when USE_WORKFLOW_CHAT flag is enabled.
 * Uses Hub-and-Spoke WebSocket (UserWebSocket) instead of HTTP SSE.
 */

import { axiom } from '../lib/axiom';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useUserWebSocket } from '../contexts/UserWebSocketContext';
import type { StreamEvent, NodeStatus } from '@pistachiorama/shared-types';

// Timeout for agent loop to prevent infinite waiting if workflow dies silently
// Set to 5.5 minutes - slightly longer than backend reaper (5min) to allow backend cleanup first
const AGENT_LOOP_TIMEOUT_MS = 5.5 * 60 * 1000;

// Timeout for message delivery confirmation (message_received or agent_started)
// If neither arrives within this window, the message was likely lost.
// Set to 30s: fast-path ack from Rust DO arrives in <1s for 99%+ of cases.
// 30s provides margin for edge cases (hibernated Rust DO wake-up + broadcast rebuild).
const MESSAGE_DELIVERY_TIMEOUT_MS = 30_000;

export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  skipped?: boolean;
}

export interface ToolResult {
  tool_call_id: string;
  success: boolean;
  result?: unknown;
  error?: string;
  pending_approval?: boolean;
}

export interface RateLimitApproval {
  allowedCount: number;
  blockedToolCalls: ToolCall[];
  resourceType: 'node';
}

export interface AgentLoopResult {
  complete: boolean;
  paused?: boolean;
  stopped?: boolean;
  content?: string;
  thinking?: string | null;
  pendingToolCall?: ToolCall;
  allToolCalls?: ToolCall[];
  allToolResults?: ToolResult[];
  error?: string;
  saved?: boolean;
}

interface UseWorkflowChatOptions {
  streamId: string;
  nodeId?: string;
  onAutoLayout?: () => Promise<void>;
  onStatusChanged?: (nodeId: string, status: NodeStatus) => void;
  onContentChunk?: (text: string) => void;
}

export interface UseWorkflowChatReturn {
  // State
  pendingApproval: ToolCall | null;
  rateLimitApproval: RateLimitApproval | null;
  autoApprove: boolean;
  isLooping: boolean;
  loopingStatus: string | null;
  streamingContent: string;
  streamingThinking: string;
  error: string | null;

  // Actions
  runAgentLoop: (message: string) => Promise<AgentLoopResult>;
  continueAfterApproval: (approved: boolean, reason?: string) => Promise<AgentLoopResult | null>;
  handleRateLimitApproval: (allow: boolean) => Promise<AgentLoopResult | null>;
  setAutoApprove: (value: boolean) => void;
  stopAgentLoop: () => void;
}

export function useWorkflowChat(options: UseWorkflowChatOptions): UseWorkflowChatReturn {
  const { streamId, nodeId, onAutoLayout, onContentChunk, onStatusChanged } = options;
  const {
    userWebSocket,
    status: wsStatus,
    error: wsError,
    errorStreamId: wsErrorStreamId,
    sendMessage,
    approveToolCall,
    rejectToolCall,
    cancelAgentLoop,
    // subscribe - available but not used here (subscription managed by context)
    isSubscribed,
  } = useUserWebSocket();

  // State
  const [pendingApproval, setPendingApproval] = useState<ToolCall | null>(null);
  const [rateLimitApproval, setRateLimitApproval] = useState<RateLimitApproval | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [loopingStatus, setLoopingStatus] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingThinking, setStreamingThinking] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Refs for resolving promises
  const resolveRef = useRef<((result: AgentLoopResult) => void) | null>(null);
  const toolCallsRef = useRef<ToolCall[]>([]);
  const toolResultsRef = useRef<ToolResult[]>([]);
  const accumulatedContentRef = useRef('');
  const accumulatedThinkingRef = useRef('');
  const currentIterationRef = useRef<number | undefined>(undefined);
  const currentTurnIdRef = useRef<string | undefined>(undefined);
  const loopActiveRef = useRef(false);
  const lastSeqRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deliveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear both timeout guards (agent loop + delivery confirmation).
  const clearAllTimeouts = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (deliveryTimeoutRef.current) {
      clearTimeout(deliveryTimeoutRef.current);
      deliveryTimeoutRef.current = null;
    }
  };

  // Clear all per-turn accumulated refs. Called after every terminal event
  // (agent_complete, agent_cancelled, error) and before starting a new loop.
  // Prevents stale content from leaking into the next agent loop.
  const clearAccumulatedRefs = () => {
    accumulatedContentRef.current = '';
    accumulatedThinkingRef.current = '';
    currentIterationRef.current = undefined;
    toolCallsRef.current = [];
    toolResultsRef.current = [];
  };

  // Resolve the agent loop promise on any terminal event. Shared by
  // agent_complete, agent_cancelled, error (non-recoverable), and wsError.
  const resolveTerminal = (result: AgentLoopResult) => {
    clearAllTimeouts();
    setIsLooping(false);
    setLoopingStatus(null);
    loopActiveRef.current = false;
    currentTurnIdRef.current = undefined;
    resolveRef.current?.(result);
    resolveRef.current = null;
    clearAccumulatedRefs();
  };

  // Spec 087: Refs for values that change during streaming to prevent handler re-registration
  // Using refs instead of closure values keeps handler stable during state changes
  const autoApproveRef = useRef(autoApprove);
  const pendingApprovalRef = useRef(pendingApproval);
  const callbacksRef = useRef({ onAutoLayout, onContentChunk, onStatusChanged });
  const prevAutoApproveRef = useRef(autoApprove); // Track previous value for logging

  // Keep refs in sync with latest values
  useEffect(() => {
    autoApproveRef.current = autoApprove;
    pendingApprovalRef.current = pendingApproval;
  }, [autoApprove, pendingApproval]);

  // Spec 087: Observability - log when autoApprove ACTUALLY changes during streaming
  useEffect(() => {
    if (isLooping && prevAutoApproveRef.current !== autoApprove) {
      axiom.info('[useWorkflowChat] autoApprove changed during streaming', {
        event: 'chat_state_change_while_streaming',
        stream_id: streamId.slice(0, 8),
        node_id: nodeId?.slice(0, 8),
        auto_approve: autoApprove,
        handler_remains_stable: true, // Thanks to ref-based fix
      });
    }
    // Always keep ref in sync, even when not looping
    prevAutoApproveRef.current = autoApprove;
  }, [autoApprove, isLooping, streamId, nodeId]);

  useEffect(() => {
    callbacksRef.current = { onAutoLayout, onContentChunk, onStatusChanged };
  }, [onAutoLayout, onContentChunk, onStatusChanged]);

  // Handle incoming stream events
  const handleStreamEvent = useCallback((eventStreamId: string, event: StreamEvent) => {
    if (eventStreamId !== streamId) return;

    // Filter by nodeId to prevent cross-node event leakage.
    // When nodeId is undefined (stream-level subscription), accept all events.
    // When nodeId is set, only accept events matching that node.
    const eventNodeId = (event as { node_id?: string }).node_id;
    if (nodeId !== undefined && eventNodeId !== nodeId) return;

    // Update sequence number for reconnection
    if (event.seq && event.seq > lastSeqRef.current) {
      lastSeqRef.current = event.seq;
    }

    switch (event.type) {
      case 'message_received':
        // Delivery confirmation: backend accepted our message for processing
        if (deliveryTimeoutRef.current) {
          clearTimeout(deliveryTimeoutRef.current);
          deliveryTimeoutRef.current = null;
        }
        setLoopingStatus('Processing...');
        break;

      case 'agent_started':
        // Spec 166 Phase 2: Ignore duplicate agent_started within the same loop.
        // The mailbox catch-up path can deliver a second copy mid-stream,
        // which would clear accumulated content and cause garbled text (#4859).
        if (loopActiveRef.current) {
          axiom.warn('[useWorkflowChat] Duplicate agent_started ignored', {
            event: 'chat_duplicate_agent_started',
            stream_id: streamId.slice(0, 8),
            node_id: nodeId?.slice(0, 8),
          });
          break;
        }
        loopActiveRef.current = true;
        currentTurnIdRef.current = (event as { turn_id?: string }).turn_id;
        // Clear delivery timeout (fallback in case message_received wasn't sent)
        if (deliveryTimeoutRef.current) {
          clearTimeout(deliveryTimeoutRef.current);
          deliveryTimeoutRef.current = null;
        }
        setIsLooping(true);
        setStreamingContent('');
        setStreamingThinking('');
        setError(null);
        setLoopingStatus('Thinking...');
        clearAccumulatedRefs();
        break;

      case 'node_status_changed':
        // Notify parent component of status change
        // Note: StreamsContext handles global state updates
        // Spec 087: Use ref to get current callback without recreating handler
        callbacksRef.current.onStatusChanged?.(event.node_id, event.status);
        break;

      case 'content': {
        const text = (event as { text?: string }).text || '';
        const eventIteration = (event as { iteration?: number }).iteration;
        const eventTurnId = (event as { turn_id?: string }).turn_id;

        // Ignore content from a concurrent loop on the same node.
        // Without this, overlapping loops (e.g., /loop command) interleave content.
        if (currentTurnIdRef.current && eventTurnId && eventTurnId !== currentTurnIdRef.current) {
          axiom.warn('[useWorkflowChat] Content from wrong turn_id ignored', {
            event: 'chat_content_wrong_turn_id',
            stream_id: streamId.slice(0, 8),
            expected_turn_id: currentTurnIdRef.current.slice(0, 8),
            received_turn_id: eventTurnId.slice(0, 8),
          });
          break;
        }

        // Reset content when iteration changes (multi-iteration loops with tool calls).
        // Each LLM iteration produces a complete response — only the latest matters.
        // Without this, content from iteration 0 ("Let me look that up...") concatenates
        // with iteration 1 ("The answer is..."), producing garbled text.
        if (eventIteration !== undefined &&
            currentIterationRef.current !== undefined &&
            eventIteration !== currentIterationRef.current) {
          accumulatedContentRef.current = '';
          setStreamingContent('');
        }
        if (eventIteration !== undefined) {
          currentIterationRef.current = eventIteration;
        }

        setStreamingContent(prev => prev + text);
        accumulatedContentRef.current += text;

        // Spec 087: Use ref to get current callback without recreating handler
        callbacksRef.current.onContentChunk?.(text);
        break;
      }

      case 'thinking': {
        const thinkingText = (event as { text?: string }).text || '';
        const thinkingTurnId = (event as { turn_id?: string }).turn_id;

        // Same turn_id isolation as content — ignore thinking from concurrent loops
        if (currentTurnIdRef.current && thinkingTurnId && thinkingTurnId !== currentTurnIdRef.current) {
          break;
        }

        accumulatedThinkingRef.current += thinkingText;
        setStreamingThinking(prev => prev + thinkingText);
        break;
      }

      case 'tool_requested': {
        const toolEvent = event as unknown as { tool_call: ToolCall; requires_approval?: boolean };
        toolCallsRef.current.push(toolEvent.tool_call);
        setLoopingStatus(`Running: ${toolEvent.tool_call.name}`);

        if (toolEvent.requires_approval) {
          // Spec 087: Use ref to get current autoApprove value without recreating handler
          if (autoApproveRef.current) {
            // Auto-approve: immediately send approval to prevent workflow stall
            approveToolCall(streamId, toolEvent.tool_call.id);
          } else {
            setPendingApproval(toolEvent.tool_call);
            setLoopingStatus(`Waiting for approval: ${toolEvent.tool_call.name}`);
          }
        }
        break;
      }

      case 'tool_executed': {
        const execEvent = event as unknown as {
          tool_call_id: string;
          status: string;
          result?: unknown;
          error?: string;
          rate_limited?: boolean;
        };

        toolResultsRef.current.push({
          tool_call_id: execEvent.tool_call_id,
          success: execEvent.status === 'success',
          result: execEvent.result,
          error: execEvent.error,
        });

        // Handle rate limit approval
        if (execEvent.rate_limited) {
          const blockedCalls = toolCallsRef.current.filter(
            tc => tc.name === 'create_node' && !toolResultsRef.current.find(r => r.tool_call_id === tc.id)
          );
          setRateLimitApproval({
            allowedCount: 3,
            blockedToolCalls: blockedCalls,
            resourceType: 'node',
          });
        }
        break;
      }

      case 'tool_approval_response': {
        const approvalEvent = event as unknown as { tool_call_id: string; approved: boolean };
        // Spec 087: Use ref to avoid stale closure
        if (pendingApprovalRef.current?.id === approvalEvent.tool_call_id) {
          setPendingApproval(null);
          setLoopingStatus('Continuing...');
        }
        break;
      }

      case 'agent_complete': {
        setStreamingContent('');
        setStreamingThinking('');

        // Prefer terminal payload (agent_complete.message.thinking) as authoritative source
        // because it is the final formatted value from the backend (with correct iteration
        // separators). Fall back to accumulatedThinkingRef if terminal payload is null/missing
        // (e.g., non-thinking model responses or old server versions).
        const terminalThinking = (event as { message?: { thinking?: string | null } }).message?.thinking ?? null;
        const capturedThinking = terminalThinking ?? accumulatedThinkingRef.current ?? null;

        resolveTerminal({
          complete: true,
          content: accumulatedContentRef.current,
          thinking: capturedThinking,
          allToolCalls: toolCallsRef.current,
          allToolResults: toolResultsRef.current,
          saved: true,
        });

        callbacksRef.current.onAutoLayout?.();
        break;
      }

      case 'agent_cancelled': {
        setPendingApproval(null);
        resolveTerminal({
          complete: false,
          stopped: true,
          content: accumulatedContentRef.current,
          allToolCalls: toolCallsRef.current,
          allToolResults: toolResultsRef.current,
          saved: false,
        });
        break;
      }

      case 'error': {
        const errEvent = event as unknown as { code: string; message: string; recoverable?: boolean };
        setError(errEvent.message);

        if (!errEvent.recoverable) {
          resolveTerminal({
            complete: false,
            error: errEvent.message,
            content: accumulatedContentRef.current,
            allToolCalls: toolCallsRef.current,
            allToolResults: toolResultsRef.current,
            saved: false,
          });
        }
        break;
      }
    }
    // Spec 087: Only include stable dependencies to prevent handler re-registration
    // Changed dependencies (autoApprove, onAutoLayout, onContentChunk, onStatusChanged)
    // are accessed via refs to maintain handler stability during streaming
  }, [streamId, nodeId, approveToolCall]);

  // Subscribe to stream events
  // Uses specific handler removal to avoid breaking other listeners (e.g., graph updates)
  useEffect(() => {
    if (!userWebSocket) return;
    userWebSocket.on('streamEvent', handleStreamEvent);
    return () => {
      userWebSocket.off('streamEvent', handleStreamEvent);
    };
  }, [userWebSocket, handleStreamEvent]);

  // Handle hard refresh
  // Uses specific handler removal to avoid breaking other listeners
  useEffect(() => {
    if (!userWebSocket) return;
    const handleHardRefresh = (refreshStreamId: string) => {
      if (refreshStreamId === streamId) {
        // Clear local state
        loopActiveRef.current = false;
        setStreamingContent('');
        setStreamingThinking('');
        clearAccumulatedRefs();
        lastSeqRef.current = 0;
      }
    };

    userWebSocket.on('hardRefreshRequired', handleHardRefresh);
    return () => userWebSocket.off('hardRefreshRequired', handleHardRefresh);
  }, [userWebSocket, streamId]);

  // Handle WebSocket errors from context (e.g., NOT_SUBSCRIBED, FORWARD_FAILED)
  // This watches the context's error state instead of registering a separate handler
  // to avoid overwriting the context's error listener.
  // Filters by streamId to prevent cross-stream error leakage.
  useEffect(() => {
    // Only react if:
    // 1. There's an error
    // 2. We have an active loop (resolveRef.current exists)
    // 3. Error is for THIS stream (or no streamId = global error)
    if (!wsError || !resolveRef.current) return;
    if (wsErrorStreamId && wsErrorStreamId !== streamId) return;

    // RPC_TIMEOUT is expected when agent loop takes >10s to start processing.
    // The agent loop continues running in the background and will broadcast events.
    // Don't treat as terminal — let the delivery/agent timeout handle it.
    if (wsError.message.includes('RPC_TIMEOUT')) {
      axiom.info('[useWorkflowChat] RPC_TIMEOUT received, agent loop may still be running', {
        event: 'chat_rpc_timeout_non_terminal',
        stream_id: streamId.slice(0, 8),
        node_id: nodeId?.slice(0, 8),
      });
      return;
    }

    axiom.error('[useWorkflowChat] WebSocket error', { stream_id: streamId.slice(0, 8), error: wsError.message });
    setError(wsError.message);
    resolveTerminal({
      complete: false,
      error: wsError.message,
      content: accumulatedContentRef.current,
      allToolCalls: toolCallsRef.current,
      allToolResults: toolResultsRef.current,
      saved: false,
    });
  }, [wsError, wsErrorStreamId, streamId, nodeId]);

  // Cleanup timeouts on unmount or stream/node change to prevent memory leaks
  useEffect(() => {
    return () => clearAllTimeouts();
  }, [streamId, nodeId]);

  // Reconnection catch-up
  // Note: UserWebSocket already re-subscribes with stored lastSeq on reconnect.
  // This effect is intentionally removed to avoid resetting subscription state.
  // See FINDING-10 in PR #384 review.

  // Actions
  const runAgentLoop = useCallback(async (message: string): Promise<AgentLoopResult> => {
    // Evaluate once to avoid double-calling mocks in tests.
    const subscribed = isSubscribed(streamId);
    if (wsStatus !== 'connected') {
      return {
        complete: false,
        error: 'WebSocket not connected',
        saved: false,
      };
    }

    // Guard: prevent sending message before subscription is initiated
    // Note: isSubscribed() returns true once subscribe() is called (optimistic),
    // not when server confirms. This is sufficient due to DO message ordering.
    if (!subscribed) {
      return {
        complete: false,
        error: 'Not subscribed to stream yet. Please wait and try again.',
        saved: false,
      };
    }

    // Reset state
    loopActiveRef.current = false;
    currentTurnIdRef.current = undefined;
    setStreamingContent('');
    setStreamingThinking('');
    setError(null);
    setPendingApproval(null);
    setRateLimitApproval(null);
    clearAccumulatedRefs();

    // Optimistic UI: Show immediate feedback before backend confirms
    setIsLooping(true);
    setLoopingStatus('Sending...');

    // Send message via WebSocket
    const sent = sendMessage(streamId, message, nodeId);
    if (!sent) {
      // WebSocket disconnected between subscription check and send
      setIsLooping(false);
      setLoopingStatus(null);
      return {
        complete: false,
        error: 'Failed to send message - WebSocket disconnected',
        saved: false,
      };
    }

    // Return promise that resolves when agent completes (with timeout protection)
    return new Promise((resolve) => {
      clearAllTimeouts();
      resolveRef.current = resolve;

      // Set up delivery confirmation timeout (30s)
      // If neither message_received nor agent_started arrives, the message was likely lost
      deliveryTimeoutRef.current = setTimeout(() => {
        if (resolveRef.current) {
          axiom.error('[useWorkflowChat] Message delivery timed out', {
            event: 'chat_delivery_timeout',
            stream_id: streamId.slice(0, 8),
            node_id: nodeId?.slice(0, 8),
            timeout_ms: MESSAGE_DELIVERY_TIMEOUT_MS,
          });
          setError('Message delivery failed. Please try again.');
          resolveTerminal({
            complete: false,
            error: 'Message delivery failed. Please try again.',
            saved: false,
          });
        }
      }, MESSAGE_DELIVERY_TIMEOUT_MS);

      // Set up timeout to prevent infinite waiting if workflow dies silently
      timeoutRef.current = setTimeout(() => {
        if (resolveRef.current) {
          axiom.error('[useWorkflowChat] Agent loop timed out', {
            stream_id: streamId.slice(0, 8),
            node_id: nodeId?.slice(0, 8),
            timeout_ms: AGENT_LOOP_TIMEOUT_MS,
          });
          setError('Agent loop timed out');
          resolveTerminal({
            complete: false,
            error: 'Agent loop timed out. Please try again.',
            content: accumulatedContentRef.current,
            allToolCalls: toolCallsRef.current,
            allToolResults: toolResultsRef.current,
            saved: false,
          });
        }
      }, AGENT_LOOP_TIMEOUT_MS);
    });
  }, [wsStatus, streamId, nodeId, sendMessage, isSubscribed]);

  const continueAfterApproval = useCallback(async (
    approved: boolean,
    reason?: string
  ): Promise<AgentLoopResult | null> => {
    if (!pendingApproval) return null;

    if (approved) {
      approveToolCall(streamId, pendingApproval.id);
    } else {
      rejectToolCall(streamId, pendingApproval.id, reason);
    }

    setPendingApproval(null);
    return null; // Result will come via events
  }, [streamId, pendingApproval, approveToolCall, rejectToolCall]);

  const handleRateLimitApproval = useCallback(async (
    allow: boolean
  ): Promise<AgentLoopResult | null> => {
    if (!rateLimitApproval) return null;

    // Approve or reject all blocked tool calls
    for (const tc of rateLimitApproval.blockedToolCalls) {
      if (allow) {
        approveToolCall(streamId, tc.id);
      } else {
        rejectToolCall(streamId, tc.id, 'Rate limit exceeded');
      }
    }

    setRateLimitApproval(null);
    return null;
  }, [streamId, rateLimitApproval, approveToolCall, rejectToolCall]);

  const stopAgentLoop = useCallback(() => {
    cancelAgentLoop(streamId, nodeId);
  }, [streamId, nodeId, cancelAgentLoop]);

  return {
    pendingApproval,
    rateLimitApproval,
    autoApprove,
    isLooping,
    loopingStatus,
    streamingContent,
    streamingThinking,
    error,
    runAgentLoop,
    continueAfterApproval,
    handleRateLimitApproval,
    setAutoApprove,
    stopAgentLoop,
  };
}
